import React, { useState } from "react";
import { User, EmailAuthProvider, reauthenticateWithCredential, deleteUser } from "firebase/auth";
import { 
  doc, 
  setDoc, 
  deleteDoc, 
  collection, 
  getDocs, 
  getDoc,
  writeBatch,
  query,
  where,
  collectionGroup 
} from "firebase/firestore";
import { ref, deleteObject, listAll } from "firebase/storage";
import { auth, db, storage } from "../firebase/config";
import { useNavigate } from "react-router-dom";

interface DeletePopupProps {
  user: User | null;
  onClose: () => void;
}

const DeletePopup: React.FC<DeletePopupProps> = ({ user, onClose }) => {
  const [step, setStep] = useState(1); // 1: Confirm Delete, 2: Anon Choice, 3: Password Verification
  const [keepDataChoice, setKeepDataChoice] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const handleChoice = (keepData: boolean) => {
    setKeepDataChoice(keepData);
    setStep(3);
  };

  const handlePermanentDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    const liveUser = auth.currentUser || user;
    if (!liveUser || keepDataChoice === null || !password) return;
    
    setLoading(true);
    setErrorMsg("");

    try {
      const userId = liveUser.uid;

      // STEP 1: RE-AUTHENTICATE USER
      if (liveUser.email) {
        const credential = EmailAuthProvider.credential(liveUser.email, password);
        await reauthenticateWithCredential(liveUser, credential);
      } else {
        throw new Error("User email not found for re-authentication.");
      }

      // Set this immediately after re-auth succeeds — before any Firestore
      // deletes happen — so AuthContext's onSnapshot listener never mistakes
      // a mid-deletion doc removal for a real "ftuCompleted: false" state.
      sessionStorage.setItem(`bodyown_account_deleted_${userId}`, "true");

      // STEP 2: METADATA EXTRACTION
      const userDocRef = doc(db, "users", userId);
      // Fetch user data once here to check for existing images
      const userSnap = await getDoc(userDocRef);
      const userData = userSnap.exists() ? userSnap.data() : null;

      // STEP 3: STORAGE CLEANUP (SMART DELETE)
      try {
        // Only call deleteObject if the database indicates an image exists.
        // This prevents the browser from logging a 404 error in the network tab.
        if (userData?.profileImageUrl) {
          const profilePicRef = ref(storage, `profileImages/${userId}`);
          await deleteObject(profilePicRef).catch((err) => {
            // Secondary safety check: if it's still a 404, ignore it.
            if (err.code !== "storage/object-not-found") throw err;
          });
        }
      } catch (profileErr) {
        console.warn("Non-blocking profile image cleanup skip:", profileErr);
      }

      if (!keepDataChoice) { 
        try {
          const voiceNotesFolderRef = ref(storage, `voiceNotes/${userId}`);
          const folderContents = await listAll(voiceNotesFolderRef);
          
          if (folderContents.items.length > 0) {
            const voiceDeletes = folderContents.items.map((fileRef) => 
              deleteObject(fileRef).catch((err) => {
                if (err.code !== "storage/object-not-found") throw err;
              })
            );
            await Promise.all(voiceDeletes);
          }
        } catch (storageErr) {
          console.warn("Voice notes storage cleanup skipped or restricted:", storageErr);
        }
      }

      // STEP 4: CLEAN UP GLOBAL FEEDBACK RECORDS (Scrub email only)
      try {
        const feedbackQuery = query(
          collectionGroup(db, "reports"), 
          where("userId", "==", userId)
        );
        const feedbackSnapshot = await getDocs(feedbackQuery);

        if (!feedbackSnapshot.empty) {
          const feedbackBatch = writeBatch(db);
          feedbackSnapshot.docs.forEach((feedbackDoc) => {
            feedbackBatch.update(feedbackDoc.ref, {
              email: ""
            });
          });
          await feedbackBatch.commit();
        }
      } catch (feedbackErr) {
        // This usually fails if the Index hasn't been created in Firebase Console yet
        console.error("Non-blocking feedback cleanup error (Check if index is created):", feedbackErr);
      }

      // STEP 5: FIRESTORE USER DOCUMENT TREE HANDLING
      if (keepDataChoice) {
        // --- PATH A: ANONYMIZE ---
        await setDoc(userDocRef, {
          fullName: "Anonymized User",
          firstName: null,
          lastName: null,
          email: null,
          profileImageUrl: null,
          agreeAnonymisedData: true,
        }, { merge: true });

        await deleteDoc(doc(db, "streaks", userId)).catch(() => {});
        
      } else {
        // --- PATH B: HARD DELETE (TOTAL INFRASTRUCTURE PURGE) ---
        const flatUserSubcollections = [
          "journalEntries",
          "recoveryDailyLogs",
          "recoveryWeeklyActivities",
          "answers",
          "recoveryProgrammes",
          "clinical_notes"
        ];

        for (const subName of flatUserSubcollections) {
          try {
            const subRef = collection(db, "users", userId, subName);
            const snapshot = await getDocs(subRef);
            if (!snapshot.empty) {
              const batch = writeBatch(db);
              snapshot.docs.forEach((d) => batch.delete(d.ref));
              await batch.commit();
            }
          } catch {
            console.warn(`Skipped subcollection '${subName}' due to restriction or lack of documents.`);
          }
        }

        try {
          const progressRef = collection(db, "users", userId, "progress");
          const progressSnapshot = await getDocs(progressRef);
          if (!progressSnapshot.empty) {
            const progressBatch = writeBatch(db);
            
            for (const progressDoc of progressSnapshot.docs) {
              const notesRef = collection(db, "users", userId, "progress", progressDoc.id, "notes");
              const notesSnapshot = await getDocs(notesRef).catch(() => null);
              
              if (notesSnapshot && !notesSnapshot.empty) {
                notesSnapshot.docs.forEach((noteDoc) => progressBatch.delete(noteDoc.ref));
              }
              progressBatch.delete(progressDoc.ref);
            }
            await progressBatch.commit();
          }
        } catch {
          console.warn("Skipped progress subcollection nested loop cleaning.");
        }

        const questionnaireSubcollections = [
          "responses",
          "video_responses"
        ];

        for (const subName of questionnaireSubcollections) {
          try {
            const subRef = collection(db, "questionnaires", userId, subName);
            const snapshot = await getDocs(subRef);
            if (!snapshot.empty) {
              const batch = writeBatch(db);
              snapshot.docs.forEach((d) => batch.delete(d.ref));
              await batch.commit();
            }
          } catch {
            console.warn(`Skipped questionnaire subcollection '${subName}'.`);
          }
        }

        await deleteDoc(userDocRef);
        await deleteDoc(doc(db, "questionnaires", userId));
        await deleteDoc(doc(db, "streaks", userId));
      }

      // STEP 6: AUTH LAYER REMOVAL
      sessionStorage.removeItem(`bodyown_greeting_lock_${userId}`);
      await deleteUser(liveUser);
      onClose();
      navigate("/login");

    } catch (err) {
      console.error("Deletion process failed:", err);
      const error = err as { code?: string; message?: string };
      
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        setErrorMsg("Incorrect password. Please try again.");
      } else {
        setErrorMsg("Authentication failed. Please log out, log back in, and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-6">
      
      {/* BRANDED FULL SCREEN LOADER - Triggers when deletion starts */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100%',
          backgroundColor: '#fcf9f7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483647,
          pointerEvents: 'all'
        }}>
          {/* Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(30, 94, 91, 0.1)',
            borderTop: '4px solid #1e5e5b',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '20px',
            boxSizing: 'border-box'
          }} />

          {/* Branding */}
          <div style={{
            fontFamily: 'DM Sans, sans-serif',
            textAlign: 'center'
          }}>
            <p style={{
              color: '#0c2624',
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              margin: 0,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              Wiping Account
            </p>
          </div>

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            body { overflow: hidden !important; }
          `}</style>
        </div>
      )}

      <div className="bg-[#FCF9F7] rounded-[32px] p-10 w-full max-w-[440px] shadow-xl">
        
        {/* STEP 1: INITIAL CONFIRMATION */}
        {step === 1 && (
          <div className="flex flex-col">
             <div className="flex items-center gap-4 mb-6">
               <span className="text-[32px]" aria-hidden="true">⚠️</span>
               <h1 className="text-[24px] font-bold text-[#0C2624]">Delete account?</h1>
             </div>
             <p className="mb-8 text-[#4A4A4A] text-sm subpixel-antialiased leading-relaxed">
               This will permanently remove your login and contact details.
             </p>
             <button 
               onClick={() => setStep(2)} 
               className="w-full py-4 bg-[#6C7A52] text-white rounded-[16px] font-bold transition-all active:scale-95 shadow-md hover:bg-[#5B6943]"
             >
               Continue
             </button>
             <button 
               onClick={onClose} 
               className="w-full py-4 mt-2 text-gray-500 font-semibold transition-colors hover:text-gray-700"
             >
               Cancel
             </button>
          </div>
        )}

        {/* STEP 2: DATA DONATION CHOICE */}
        {step === 2 && (
          <div className="flex flex-col">
             <div className="flex items-center gap-4 mb-6">
               <span className="text-[32px]" aria-hidden="true">🗑️</span>
               <h1 className="text-[24px] font-bold text-[#0C2624]">Anonymize data?</h1>
             </div>
             <p className="mb-8 text-[#4A4A4A] text-sm subpixel-antialiased leading-relaxed">
               Would you like to contribute your (anonymized) data and voice notes to help improve our AI models for other women?
             </p>
             <button 
               onClick={() => handleChoice(true)} 
               className="w-full py-4 bg-[#6C7A52] text-white rounded-[16px] font-bold mb-3 transition-all active:scale-95 shadow-md hover:bg-[#5B6943]"
             >
               YES (Keep & Anonymize)
             </button>
             <button 
               onClick={() => handleChoice(false)} 
               className="w-full py-4 border-2 border-[#6C7A52]/20 text-[#6C7A52] rounded-[16px] font-bold transition-all active:scale-95 hover:bg-[#6C7A52]/5"
             >
               NO (Delete Everything)
             </button>
             <button 
               onClick={onClose} 
               className="w-full py-4 mt-2 text-gray-500 font-semibold transition-colors hover:text-gray-700"
             >
               Cancel
             </button>
          </div>
        )}

        {/* STEP 3: RE-AUTHENTICATION GATEWAY */}
        {step === 3 && (
          <form onSubmit={handlePermanentDelete} className="flex flex-col">
             <div className="flex items-center gap-4 mb-6">
               <span className="text-[32px]" aria-hidden="true">🔒</span>
               <h1 className="text-[24px] font-bold text-[#0C2624]">Confirm Password</h1>
             </div>
             <p className="mb-6 text-[#4A4A4A] text-sm subpixel-antialiased leading-relaxed">
               For security, please enter your password to authorize deleting your authentication account from our systems.
             </p>
             
             <input
               type="password"
               placeholder="Enter your current password"
               value={password}
               onChange={(e) => setPassword(e.target.value)}
               className="w-full px-4 py-3 mb-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#6C7A52] focus:ring-1 focus:ring-[#6C7A52] disabled:opacity-50"
               required
               disabled={loading}
             />

             {errorMsg && <p className="text-red-600 text-sm font-semibold mb-4">{errorMsg}</p>}

             <button 
               type="submit" 
               disabled={loading || !password} 
               className="w-full py-4 bg-[#6C7A52] disabled:bg-gray-300 text-white rounded-[16px] font-bold transition-all active:scale-95 shadow-md hover:bg-[#5B6943]"
             >
               Confirm Permanent Deletion
             </button>
             <button 
               type="button" 
               disabled={loading} 
               onClick={onClose} 
               className="w-full py-4 mt-2 text-gray-500 font-semibold transition-colors hover:text-gray-700"
             >
               Cancel
             </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeletePopup;